import { z } from 'zod';
import { FolioCharge } from './folio.js';
import { AuditedFields, CursorPageQuery, IsoDate, IsoDateTime, Uuid, Version } from './common.js';
import { PaymentDetail, PaymentMethod, PaymentProvider, PaymentStatus } from './payments.js';
import { KitchenTicket } from './printing.js';
/**
 * Commandes POS (ADR-016, D-H09) : une commande est un **compte ouvert** dont
 * les lignes sont figées (prix, libellé, options) au moment de l'ajout.
 * Cycle : `OPEN → PAID | CANCELLED` (`TRANSFERRED` réservé à H3).
 */
export const OrderStatus = z.enum(['OPEN', 'PAID', 'TRANSFERRED', 'CANCELLED']);
/** État de préparation dérivé des lignes (jamais saisi). */
export const PreparationStatus = z.enum(['NEW', 'IN_PREPARATION', 'READY', 'SERVED']);
export const OrderItemStatus = z.enum(['PENDING', 'SENT', 'READY', 'SERVED', 'CANCELLED']);
/** `COUNTER` : service au comptoir, sans table (Horevia Bar, ADR-019, R-BAR-01). */
export const ServiceType = z.enum(['DINE_IN', 'TAKEAWAY', 'DELIVERY', 'ROOM_SERVICE', 'COUNTER']);
export const OrderDiscountType = z.enum(['NONE', 'PERCENT', 'AMOUNT']);
/** Option choisie, figée dans la ligne (R-POS-01). */
export const OrderItemOption = z.object({
    groupId: Uuid.nullable(),
    groupName: z.string(),
    optionId: Uuid.nullable(),
    optionName: z.string(),
    priceDelta: z.number(),
});
export const OrderItem = z.object({
    id: Uuid,
    productId: Uuid.nullable(),
    productCode: z.string().nullable(),
    label: z.string(),
    quantity: z.number(),
    /** Prix unitaire de base, hors options. */
    unitPrice: z.number(),
    options: z.array(OrderItemOption),
    /** Instructions pour la préparation (« sans oignons »). */
    notes: z.string().nullable(),
    /** `quantity × (unitPrice + Σ options)`. */
    amount: z.number(),
    taxRuleCodes: z.array(z.string()),
    /**
     * Provenance du prix (ADR-019, R-BAR-03) : prix carte au moment de l'ajout et
     * règle appliquée, renseignés seulement si une happy hour a joué.
     */
    listUnitPrice: z.number().nullable().default(null),
    priceRuleId: Uuid.nullable().default(null),
    priceRuleLabel: z.string().nullable().default(null),
    status: OrderItemStatus,
    printDestinationId: Uuid.nullable(),
    kitchenTicketId: Uuid.nullable(),
    position: z.number().int(),
    sentAt: IsoDateTime.nullable(),
    readyAt: IsoDateTime.nullable(),
    servedAt: IsoDateTime.nullable(),
    cancelledAt: IsoDateTime.nullable(),
    cancellationReason: z.string().nullable(),
    createdAt: IsoDateTime,
});
export const OrderTaxDetail = z.object({
    code: z.string(),
    label: z.string(),
    rate: z.number(),
    isInclusive: z.boolean(),
    base: z.number(),
    amount: z.number(),
});
export const OrderPayment = z.object({
    id: Uuid,
    reference: z.string(),
    method: PaymentMethod,
    provider: PaymentProvider.nullable(),
    amount: z.number(),
    paidAt: IsoDateTime,
    status: PaymentStatus,
    receiptNumber: z.string().nullable(),
    recordedByName: z.string().nullable(),
});
/** Client rattaché à une commande (ADR-025). */
export const OrderCustomer = z.object({
    id: Uuid,
    displayName: z.string(),
    kind: z.enum(['PERSON', 'COMPANY']),
});
/** Solde et palier du client rattaché, résolus par le module `crm` (D-H64). */
export const OrderLoyalty = z.object({
    balance: z.number().int(),
    tierCode: z.string().nullable(),
    tierName: z.string().nullable(),
    /** Valeur d'un point à l'utilisation, devise de la commande. */
    pointValue: z.number(),
});
export const Order = z
    .object({
    id: Uuid,
    propertyId: Uuid,
    outletId: Uuid,
    outletName: z.string(),
    /** `CMD-AAAA-NNNNNN`, séquence sans trou par tenant (ADR-008). */
    reference: z.string(),
    /** Numéro court du jour par point de vente (`#12`), pour la cuisine et le client. */
    dailyNumber: z.number().int(),
    serviceDate: IsoDate,
    tableId: Uuid.nullable(),
    tableCode: z.string().nullable(),
    /** Nom de la note au comptoir (« Moussa », « chemise bleue ») — ADR-019, R-BAR-02. */
    tabName: z.string().nullable().default(null),
    /** Client rattaché (Horevia CRM, ADR-025, R-CRM-01) ; hérité du séjour à l'imputation. */
    customerId: Uuid.nullable().default(null),
    customer: OrderCustomer.nullable().default(null),
    /** Compte de fidélité du client rattaché, seulement avec le module `crm` (D-H64). */
    loyalty: OrderLoyalty.nullable().default(null),
    covers: z.number().int().nullable(),
    serviceType: ServiceType,
    status: OrderStatus,
    preparationStatus: PreparationStatus,
    currencyCode: z.string().length(3),
    subtotal: z.number(),
    discountType: OrderDiscountType,
    discountValue: z.number(),
    discountTotal: z.number(),
    discountReason: z.string().nullable(),
    taxTotal: z.number(),
    total: z.number(),
    paidTotal: z.number(),
    balance: z.number(),
    itemsCount: z.number().int(),
    billRequestedAt: IsoDateTime.nullable(),
    notes: z.string().nullable(),
    serverId: Uuid.nullable(),
    serverName: z.string().nullable(),
    openedAt: IsoDateTime,
    closedAt: IsoDateTime.nullable(),
    cancelledAt: IsoDateTime.nullable(),
    cancellationReason: z.string().nullable(),
    /** Imputation à un séjour (statut `TRANSFERRED`, ADR-018) : séjour, charge de folio, date. */
    stayReservationId: Uuid.nullable().default(null),
    stayReference: z.string().nullable().default(null),
    folioChargeId: Uuid.nullable().default(null),
    transferredAt: IsoDateTime.nullable().default(null),
    version: Version,
})
    .merge(AuditedFields);
export const OrderDetail = Order.extend({
    items: z.array(OrderItem),
    taxes: z.array(OrderTaxDetail),
    payments: z.array(OrderPayment),
    tickets: z.array(KitchenTicket),
});
// ---------------------------------------------------------------------------
// Requêtes
// ---------------------------------------------------------------------------
/** Le comptoir ne porte pas de table (R-BAR-01). */
function counterWithoutTable(input, ctx) {
    if (input.serviceType === 'COUNTER' && input.tableId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['tableId'],
            message: 'Une commande au comptoir ne porte pas de table',
        });
    }
}
export const CreateOrderRequest = z
    .object({
    outletId: Uuid,
    tableId: Uuid.nullable().optional(),
    tabName: z.string().trim().min(1).max(60).nullable().optional(),
    /** Client rattaché (ADR-025) ; `null` pour retirer. */
    customerId: Uuid.nullable().optional(),
    covers: z.number().int().min(1).max(200).nullable().optional(),
    serviceType: ServiceType.default('DINE_IN'),
    notes: z.string().trim().max(500).nullable().optional(),
})
    .superRefine(counterWithoutTable);
export const UpdateOrderRequest = z
    .object({
    tableId: Uuid.nullable().optional(),
    tabName: z.string().trim().min(1).max(60).nullable().optional(),
    customerId: Uuid.nullable().optional(),
    covers: z.number().int().min(1).max(200).nullable().optional(),
    serviceType: ServiceType.optional(),
    notes: z.string().trim().max(500).nullable().optional(),
    version: Version,
})
    .superRefine(counterWithoutTable);
/** Choix d'options pour une ligne : un groupe → une ou plusieurs options. */
export const OrderItemOptionChoice = z.object({
    groupId: Uuid,
    optionIds: z.array(Uuid).min(1).max(20),
});
export const AddOrderItemRequest = z.object({
    productId: Uuid,
    quantity: z.number().positive().max(999).default(1),
    options: z.array(OrderItemOptionChoice).max(10).default([]),
    notes: z.string().trim().max(200).nullable().optional(),
    version: Version,
});
/** Modification d'une ligne **non envoyée** (quantité, instructions, options). */
export const UpdateOrderItemRequest = z.object({
    quantity: z.number().positive().max(999).optional(),
    options: z.array(OrderItemOptionChoice).max(10).optional(),
    notes: z.string().trim().max(200).nullable().optional(),
    version: Version,
});
export const RemoveOrderItemRequest = z.object({ version: Version });
/** Annulation d'une ligne déjà envoyée : motif obligatoire (R-POS-02). */
export const CancelOrderItemRequest = z.object({
    reason: z.string().trim().min(5).max(500),
    version: Version,
});
/** Passage « prêt » / « servi » (cuisine, service) — sans verrou : plusieurs postes en parallèle. */
export const UpdateOrderItemStatusRequest = z.object({
    status: z.enum(['READY', 'SERVED']),
});
export const UpdateKitchenTicketStatusRequest = z.object({
    status: z.enum(['READY', 'SERVED']),
});
export const SendOrderRequest = z.object({ version: Version });
/** « La même chose » : répète la dernière tournée envoyée au prix courant (ADR-019, R-BAR-06). */
export const RepeatRoundRequest = z.object({ version: Version });
export const ApplyOrderDiscountRequest = z
    .object({
    discountType: OrderDiscountType,
    discountValue: z.number().nonnegative().default(0),
    reason: z.string().trim().max(500).nullable().optional(),
    version: Version,
})
    .superRefine((input, ctx) => {
    if (input.discountType !== 'NONE') {
        if (input.discountValue <= 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['discountValue'],
                message: 'Le montant de la remise doit être strictement positif',
            });
        }
        if (!input.reason || input.reason.trim().length < 5) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['reason'],
                message: 'Motif obligatoire (5 caractères minimum)',
            });
        }
        if (input.discountType === 'PERCENT' && input.discountValue > 100) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['discountValue'],
                message: 'Une remise ne dépasse pas 100 %',
            });
        }
    }
});
export const CancelOrderRequest = z.object({
    reason: z.string().trim().min(5).max(500),
    version: Version,
});
export const RequestBillRequest = z.object({ version: Version });
/**
 * Moyens de paiement proposés par la caisse quand le point de vente n'a pas de
 * réglage (`outlets.settings.paymentMethods`, ADR-026, D-H78) : comportement
 * historique de la caisse.
 */
export const POS_DEFAULT_PAYMENT_METHODS = [
    'CASH',
    'MOBILE_MONEY',
    'CARD',
    'OTHER',
];
/**
 * Moyens de paiement réglés pour un point de vente, dans l'ordre d'affichage
 * (doublons et valeurs inconnues ignorés), ou `null` sans réglage valide.
 */
export function configuredPaymentMethods(settings) {
    const raw = settings?.['paymentMethods'];
    if (!Array.isArray(raw))
        return null;
    const methods = [];
    for (const value of raw) {
        const parsed = PaymentMethod.safeParse(value);
        if (parsed.success && !methods.includes(parsed.data))
            methods.push(parsed.data);
    }
    return methods.length > 0 ? methods : null;
}
/** Moyens proposés par la caisse : le réglage du point de vente, sinon la liste par défaut. */
export function outletPaymentMethods(settings) {
    return configuredPaymentMethods(settings) ?? [...POS_DEFAULT_PAYMENT_METHODS];
}
/** Encaissement d'une commande (H2.4) — idempotent via `Idempotency-Key`. */
export const RecordOrderPaymentRequest = z.object({
    amount: z.number().positive(),
    method: PaymentMethod,
    provider: PaymentProvider.nullable().optional(),
    transactionReference: z.string().trim().max(80).nullable().optional(),
    /** Espèces remises par le client (affichage de la monnaie à rendre) ; jamais enregistré comme encaissé. */
    tenderedAmount: z.number().positive().nullable().optional(),
    notes: z.string().trim().max(500).nullable().optional(),
});
/** Réponse de `POST /orders/:id/payments` : commande à jour, paiement, monnaie à rendre. */
export const OrderPaymentResult = z.object({
    order: OrderDetail,
    payment: PaymentDetail,
    /** Espèces : montant remis − montant encaissé (`null` pour les autres moyens). */
    changeDue: z.number().nullable(),
});
/** Résultat d'une impression directe (ticket de caisse, test de destination). */
export const PrintResult = z.object({
    printed: z.boolean(),
    content: z.string(),
});
export const ListOrdersQuery = CursorPageQuery.extend({
    propertyId: Uuid.optional(),
    outletId: Uuid.optional(),
    status: OrderStatus.optional(),
    preparationStatus: PreparationStatus.optional(),
    tableId: Uuid.optional(),
    serverId: Uuid.optional(),
    /** Commandes rattachées à ce client (ADR-025). */
    customerId: Uuid.optional(),
    serviceType: ServiceType.optional(),
    /** Date de service (jour civil du point de vente), bornes incluses. */
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    /** Référence, numéro du jour (`#12`) ou nom de note. */
    q: z.string().trim().max(80).optional(),
});
export const OrdersSummaryQuery = z.object({
    propertyId: Uuid.optional(),
    outletId: Uuid.optional(),
    from: IsoDate,
    to: IsoDate,
});
export const OrdersSummary = z.object({
    from: IsoDate,
    to: IsoDate,
    currencyCode: z.string().length(3),
    ordersCount: z.number().int(),
    openCount: z.number().int(),
    paidCount: z.number().int(),
    cancelledCount: z.number().int(),
    /** Total TTC des commandes encaissées (statut PAID) sur la période. */
    revenue: z.number(),
    /** Commandes imputées à un séjour (statut TRANSFERRED) : nombre et total TTC (ADR-018). */
    transferredCount: z.number().int(),
    transferredTotal: z.number(),
    /** Remises happy hour accordées sur les commandes encaissées (ADR-019, D-H25). */
    happyHourTotal: z.number().default(0),
    averageTicket: z.number(),
    coversCount: z.number().int(),
    byPaymentMethod: z.array(z.object({
        method: PaymentMethod,
        provider: PaymentProvider.nullable(),
        amount: z.number(),
        count: z.number().int(),
    })),
    topProducts: z.array(z.object({
        productId: Uuid.nullable(),
        label: z.string(),
        quantity: z.number(),
        amount: z.number(),
    })),
});
/** « Imputer au séjour » (ADR-018, R-FOL-04) : commande OPEN sans paiement vers un séjour CHECKED_IN. */
export const TransferOrderToStayRequest = z.object({
    reservationId: Uuid,
    version: Version,
});
/** Résultat de l'imputation : commande transférée, charge posée, bon de transfert (imprimé ou à afficher). */
export const OrderTransferResult = z.object({
    order: OrderDetail,
    charge: FolioCharge,
    slip: PrintResult,
});
//# sourceMappingURL=orders.js.map